/*
 * Copyright (c) 2018-2019, F5 Networks, Inc. All rights reserved.
 *
 * No part of this software may be reproduced or transmitted in any
 * form or by any means, electronic or mechanical, for any purpose,
 * without express written permission of F5 Networks, Inc.
 */
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import bind from 'apmui/decorators/bind';
import onKeyPress, { KEY_CODES } from 'apmui/util/onKeyPress';

interface IModalWindowProps {
    id: string;
    title?: string;
    closeTitle?: string;
    /** TODO: define reasonable default value for focusSelector */
    focusSelector?: string;

    onClose?: () => void;
}

class ModalWindow extends React.Component<IModalWindowProps> {
    dialogRef: React.RefObject<HTMLDivElement>;
    savedFocus: Element | null;

    constructor(props: IModalWindowProps) {
        super(props);
        this.savedFocus = document.activeElement;
        this.dialogRef = React.createRef<HTMLDivElement>();
    }

    @bind
    onOverlayClick(evt: Event | React.SyntheticEvent<HTMLDivElement>) {
        if (this.props.onClose && evt.target === evt.currentTarget) {
            this.props.onClose();
        }
    }

    @bind
    onKeyEvent() {
        if (this.props.onClose) {
            this.props.onClose();
        }
    }

    render() {
        const closeButton = this.props.onClose && (
            <button className="apmui-modal--close" onClick={this.props.onClose} title={this.props.closeTitle} />
        );

        const labelId = this.props.title && this.props.id + '-title';
        const title = this.props.title && (
            <h3 className="apmui-modal--title" id={labelId}>
                {this.props.title}
            </h3>
        );

        // disable linter warnings caused by focus tracking code
        /* eslint-disable jsx-a11y/click-events-have-key-events */
        /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
        /* eslint-disable jsx-a11y/no-noninteractive-tabindex */
        /* eslint-disable jsx-a11y/no-static-element-interactions */
        return ReactDOM.createPortal(
            <div className="apmui-modal--overlay" onClick={this.onOverlayClick} tabIndex={0}>
                <div
                    id={this.props.id}
                    className="apmui-modal"
                    ref={this.dialogRef}
                    onKeyUp={onKeyPress(this.onKeyEvent, [KEY_CODES.ESCAPE])}
                    role="dialog"
                    aria-labelledby={labelId}>
                    {closeButton}
                    {title}
                    {this.props.children}
                </div>
                <div tabIndex={0}></div>
            </div>,
            document.body
        );
    }

    setFocus(backward?: boolean) {
        if (this.dialogRef.current) {
            let focus: HTMLElement = this.dialogRef.current;
            if (this.props.focusSelector) {
                /** Poor implementation of bidirectional tabbing:
                 *  positive tabIndex values are NOT taken into account */
                const selection = this.dialogRef.current.querySelectorAll(this.props.focusSelector);
                if (selection.length > 0) {
                    const element = backward ? selection[selection.length - 1] : selection[0];
                    if (element instanceof HTMLElement) {
                        focus = element;
                    }
                }
            }
            focus.focus();
        }
    }

    @bind
    checkFocus(evt: Event) {
        if (evt instanceof FocusEvent && this.dialogRef.current) {
            const relatedTargetInside =
                evt.relatedTarget instanceof Node && this.dialogRef.current.contains(evt.relatedTarget);
            if (!relatedTargetInside) {
                const backward =
                    evt.relatedTarget instanceof HTMLElement &&
                    evt.relatedTarget.classList.contains('apmui-modal--overlay');
                window.setTimeout(this.setFocus.bind(this, backward), 10);
            }
        }
    }

    componentDidMount() {
        this.setFocus();
        document.body.classList.add('apmui-nonscrollable');
        if (this.dialogRef.current) {
            this.dialogRef.current.addEventListener('focusout', this.checkFocus);
        }
    }

    componentDidUpdate() {
        if (!document.activeElement || document.activeElement === document.body) {
            this.setFocus();
        }
    }

    componentWillUnmount() {
        if (this.dialogRef.current) {
            this.dialogRef.current.removeEventListener('focusout', this.checkFocus);
        }
        document.body.classList.remove('apmui-nonscrollable');
        if (this.savedFocus instanceof HTMLElement && document.body.contains(this.savedFocus)) {
            this.savedFocus.focus();
        }
    }
}
export default ModalWindow;
